import {
  getTariffPrice,
  getTotalPrice,
} from '@pages/CoursesCatalog/modals/CourseEnrollmentModal/utils/tariff'
import {
  isGroupPerLessonLinkTariff,
  isIndividualLessonLinkTariff,
} from '../../tariffTypes'

export { isIndividualLessonLinkTariff }

export const getIndividualLessonDurationDefault = (
  tariff?: ICourseTariff | null,
): number => {
  const duration = Number(tariff?.lesson_duration)
  if (Number.isFinite(duration) && duration >= 1) {
    return Math.min(1440, Math.trunc(duration))
  }
  return 60
}

/**
 * Цена одного занятия для ссылки.
 * hour — first_price тарифа как есть;
 * minute — в тарифе цена за минуту, в ссылке храним цену занятия.
 */
export const getIndividualLessonFirstPriceDefault = (
  tariff?: ICourseTariff | null,
  lessonDuration?: number | null,
): number => {
  const tariffPrice = Number(tariff?.first_price) || 0
  if (tariff?.type === 'minute') {
    const duration =
      Number(lessonDuration) > 0
        ? Number(lessonDuration)
        : getIndividualLessonDurationDefault(tariff)
    return Math.round(tariffPrice * duration * 100) / 100
  }
  return tariffPrice
}

export const getIndividualLessonLinkTotal = (
  firstPrice?: number | null,
  value?: number | null,
): number => {
  const lessonPrice = Number(firstPrice)
  const lessons = Number(value)
  if (
    !Number.isFinite(lessonPrice) ||
    !Number.isFinite(lessons) ||
    lessons < 1
  ) {
    return 0
  }
  return Math.round(lessonPrice * lessons * 100) / 100
}

/** Дефолты полей при выборе hour / minute тарифа. */
export const getIndividualLessonLinkFieldDefaults = (
  tariff: ICourseTariff,
) => {
  const lesson_duration = getIndividualLessonDurationDefault(tariff)
  return {
    lesson_duration,
    first_price: getIndividualLessonFirstPriceDefault(tariff, lesson_duration),
    teacher_id: null as number | null,
    salary_per_hour: null as number | null,
  }
}

/** Сброс индивидуальных полей (не hour/minute / смена курса). */
export const getIndividualLessonLinkClearedFields = () => ({
  lesson_duration: null as number | null,
  teacher_id: null as number | null,
  salary_per_hour: null as number | null,
})

type AdditionalCourseForTotal = {
  courseId?: number | null
  tariffId?: number | null
  value?: number | null
  price?: number | null
  course?: unknown
  withDemo?: boolean
}

/** Итог ссылки: first_price формы × value + доп. курсы. */
export const getIndividualLessonLinkPackageTotal = ({
  tariff,
  firstPrice,
  value,
  additionalCourses,
  extraCoursesDiscounts,
  extraCoursesDiscountUnit,
}: {
  tariff: ICourseTariff
  firstPrice?: number | null
  value?: number | null
  additionalCourses?: AdditionalCourseForTotal[]
  extraCoursesDiscounts?: number[]
  extraCoursesDiscountUnit?: string
}): number => {
  if (!value) return 0

  const courses = additionalCourses ?? []
  const coursesWithFixedPrice = courses.filter(
    (item) => item.price !== null && item.price !== undefined,
  )
  const coursesForDefaultCalculation = courses.filter(
    (item) => item.price === null || item.price === undefined,
  )
  const fixedAdditionalCoursesPrice = coursesWithFixedPrice.reduce(
    (sum, item) => {
      const price = Number(item.price)
      return sum + (Number.isFinite(price) ? price : 0)
    },
    0,
  )

  const legacyTotal =
    getTotalPrice(
      tariff,
      value,
      undefined,
      false,
      coursesForDefaultCalculation as any,
      extraCoursesDiscounts ?? [],
      extraCoursesDiscountUnit ?? 'percent',
    ) + fixedAdditionalCoursesPrice

  return (
    getIndividualLessonLinkTotal(firstPrice, value) +
    (legacyTotal - getTariffPrice(tariff, value))
  )
}

type IndividualSubmitValues = {
  first_price?: number | null
  lesson_duration?: number | null
  teacher_id?: number | null
  salary_per_hour?: number | null
}

/** Текст ошибки валидации или null. Педагог обязателен только если выбран. */
export const getIndividualLessonLinkSubmitError = (
  isActive: boolean,
  values: IndividualSubmitValues,
): string | null => {
  if (!isActive) return null

  if (
    values.teacher_id != null &&
    (values.salary_per_hour === null ||
      values.salary_per_hour === undefined ||
      Number(values.salary_per_hour) < 0)
  ) {
    return 'Укажите ЗП педагога (не меньше 0)'
  }

  return null
}

/** Поля hour / minute / group_per_lesson для create-link; иначе обнуляем duration/teacher. */
export const mapIndividualLessonFieldsForSubmit = (
  isActive: boolean,
  values: IndividualSubmitValues,
) => {
  if (!isActive) {
    return {
      first_price: values.first_price ? Number(values.first_price) : null,
      lesson_duration: null as number | null,
      teacher_id: null as number | null,
      salary_per_hour: null as number | null,
    }
  }

  const hasTeacher =
    values.teacher_id != null && values.teacher_id !== undefined

  return {
    first_price:
      values.first_price != null && values.first_price !== undefined
        ? Number(values.first_price)
        : null,
    lesson_duration:
      values.lesson_duration != null && values.lesson_duration !== undefined
        ? Math.trunc(Number(values.lesson_duration))
        : null,
    teacher_id: hasTeacher ? Number(values.teacher_id) : null,
    salary_per_hour:
      hasTeacher &&
      values.salary_per_hour != null &&
      values.salary_per_hour !== undefined
        ? Number(values.salary_per_hour)
        : null,
  }
}

/** Итоговая цена lesson-тарифов всегда уходит в create-link. */
export const resolveIndividualMainSubmitPrice = (
  isActive: boolean,
  price: unknown,
) => {
  if (!isActive) return null
  return Math.max(0, Number(price) || 0)
}

const toNullableSubmitNumber = (value: unknown): number | null => {
  if (value === undefined || value === null || value === '') return null
  const next = Number(value)
  return Number.isFinite(next) ? next : null
}

/**
 * Маппинг доп. курса hour / minute / group_per_lesson.
 * value всегда уходит (префилл слайдера); фиксация — через is_value_fixed.
 */
export const mapLessonAdditionalCourseForSubmit = <T extends object>(
  item: T & {
    value?: number | null
    is_value_fixed?: boolean | null
    price?: number | null
    discount?: number | null
    discount_mode?: 'first_only' | 'every_payment' | 'renewal_only' | null
    apply_discount_each_payment?: boolean | null
    lesson_duration?: number | null
    first_price?: number | null
    teacher_id?: number | null
    salary_per_hour?: number | null
    group_id?: number | null
  },
  tariffType?: string | null,
): T & {
  value: number
  is_value_fixed: boolean
  period_unit: null
  is_period_fixed: false
  lesson_duration: number | null
  first_price: number | null
  teacher_id: number | null
  salary_per_hour: number | null
  group_id: number | null
  discount: number | null
  discount_mode: 'first_only' | null
  apply_discount_each_payment: false
  price: number | null
} => {
  const isValueFixed = Boolean(item.is_value_fixed)
  const isGroupPerLesson = isGroupPerLessonLinkTariff(tariffType)
  const nextValue = Math.max(1, Math.min(100, Math.trunc(Number(item.value) || 1)))
  const teacherId = toNullableSubmitNumber(item.teacher_id)
  const hasTeacher = teacherId != null
  // XOR: скидка только у gpl без фиксации кол-ва.
  const canKeepDiscount = isGroupPerLesson && !isValueFixed
  const nextDiscount = canKeepDiscount
    ? item.discount === undefined || item.discount === null
      ? null
      : Math.max(0, Math.trunc(Number(item.discount) || 0)) || null
    : null
  // UI типа скидки нет → по умолчанию только на первую оплату.
  const nextDiscountMode = nextDiscount != null ? 'first_only' : null

  return {
    ...item,
    value: nextValue,
    is_value_fixed: isValueFixed,
    period_unit: null,
    is_period_fixed: false,
    lesson_duration: toNullableSubmitNumber(item.lesson_duration),
    first_price: toNullableSubmitNumber(item.first_price),
    teacher_id: hasTeacher ? teacherId : null,
    salary_per_hour: hasTeacher
      ? toNullableSubmitNumber(item.salary_per_hour)
      : null,
    group_id: toNullableSubmitNumber(item.group_id),
    discount: nextDiscount,
    discount_mode: nextDiscountMode,
    apply_discount_each_payment: false,
    price:
      item.price === undefined || item.price === null
        ? null
        : Math.max(0, Number(item.price) || 0),
  }
}

/** free / stream_one_time доп.: не форсим period value. */
export const mapSimpleAdditionalCourseForSubmit = <T extends object>(
  item: T & {
    value?: number | null
    price?: number | null
    discount?: number | null
    group_id?: number | null
  },
): T & {
  value: null
  period_unit: null
  is_period_fixed: false
  is_value_fixed: false
  group_id: number | null
  discount: null
  apply_discount_each_payment: false
  price: number | null
} => ({
  ...item,
  value: null,
  period_unit: null,
  is_period_fixed: false,
  is_value_fixed: false,
  group_id: toNullableSubmitNumber(item.group_id),
  // Цена вручную → скидку не отправляем.
  discount: null,
  apply_discount_each_payment: false,
  price:
    item.price === undefined || item.price === null
      ? null
      : Math.max(0, Number(item.price) || 0),
})
